import React, { useCallback, useMemo } from 'react'

import { Button, ButtonProps } from 'rebass'
import SText from 'components/opinionless/SText'

import { useSTheme } from 'styles/theme'
import SLoadingIndicator from './SLoadingIndicator'
import { useHover } from '../../utils/customHooks/index'
import ConditionalRender from '../../utils/conditionalRender'
import { Icon } from '@material-ui/core'
import { ga4_buttonClicked } from 'utils/ga4'

export enum SButtonType {
    Filled = 'Filled',
    Outline = 'Outline',
    Cancel = 'Cancel',
    Link = 'Link',
}

export interface SButtonProps extends ButtonProps {
    loading?: boolean
    buttonType?: SButtonType
    textStyle?: any
    googleIcon?: string
    ga4_key?: string
}

const SButton: React.FC<SButtonProps> = (props: SButtonProps) => {
    const {
        loading = false,
        buttonType = SButtonType.Filled,
        sx = {},
        textStyle: textStyleProp = {},
        ref,
        googleIcon,
        children,
        ...otherProps
    } = props
    const theme = useSTheme()

    const [hovering, hoverProps] = useHover()

    const containerStyle = useMemo(() => {
        let bg = 'buttonBg'
        let hoverBg = theme.colors.primaryColor

        if (buttonType === SButtonType.Filled) {
            bg = 'buttonBg'
            hoverBg = theme.colors.primaryColor
        } else if (buttonType === SButtonType.Outline) {
            bg = theme.colors.backgroundColor
            hoverBg = theme.colors.hoverColor
        } else if (buttonType === SButtonType.Cancel) {
            bg = theme.colors.gray150
            hoverBg = theme.colors.gray200
        } else if (buttonType === SButtonType.Link) {
            bg = null
            hoverBg = null
        }

        return {
            bg: hovering ? hoverBg : bg,
        }
    }, [buttonType, hovering])

    const textStyle = useMemo(() => {
        if (buttonType === SButtonType.Filled) {
            return {
                color: 'buttonText',
            }
        } else if (buttonType === SButtonType.Outline) {
            return {
                color: theme.colors.secondaryColor,
            }
        } else if (buttonType === SButtonType.Cancel) {
            return {
                color: theme.colors.gray400,
            }
        } else if (buttonType === SButtonType.Link) {
            return {
                color: theme.colors.secondaryColor,
            }
        } else {
            return {}
        }
    }, [buttonType])

    const loadingIndicatorColor = useMemo(() => {
        if (buttonType === SButtonType.Filled) {
            return 'buttonText'
        } else {
            return theme.colors.secondaryColor
        }
    }, [buttonType])

    const disabledOnClickCallback = useCallback(() => {
        console.log('click')
    }, [])

    return (
        <Button
            sx={{
                ...containerStyle,
                px: 6,
                borderRadius: 1,
                boxShadow: 'main',
                height: '46px',
                cursor: 'pointer',
                border: 'none',
                outline: 'none',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s',
                ...sx,
            }}
            disabled={loading}
            onClick={(e) => {
                props.onClick?.(e)
                ga4_buttonClicked(props.ga4_key)
            }}
            {...otherProps}
            {...hoverProps}
        >
            {loading && <SLoadingIndicator size={22} color={loadingIndicatorColor} />}
            <SText
                sx={{
                    ...textStyle,
                    letterSpacing: '0.02em',
                    fontWeight: 'semiBold',
                    fontSize: 3,
                    fontFamily: 'default',
                    lineHeight: '40px',
                    ...textStyleProp,
                }}
            >
                <ConditionalRender if={googleIcon}>
                    <Icon style={{ verticalAlign: 'middle', marginTop: '-5px', marginRight: '5px' }}>{googleIcon}</Icon>
                </ConditionalRender>
                {!loading && children}
            </SText>
        </Button>
    )
}

export default SButton
